import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCheckoutCart } from '../contexts/CheckoutCartContext';
import useProductCatalog from './useProductCatalog';
import { saveContactToSession } from '../utils/aiPromptStorage';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';

const normalizeContact = (contact = {}) => ({
  name: contact.name || '',
  email: contact.email || '',
  phone: contact.phone || '',
});

const normalizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }
  try {
    return JSON.parse(JSON.stringify(metadata));
  } catch (error) {
    console.warn('[useCheckoutRedirect] Unable to serialize metadata payload', error);
    return undefined;
  }
};

const ensureProductList = (products) => {
  if (!Array.isArray(products)) {
    return [];
  }
  return products
    .map((product) => {
      if (!product || typeof product.productSlug !== 'string') {
        return null;
      }
      return {
        productSlug: product.productSlug,
        planKey: typeof product.planKey === 'string' ? product.planKey : null,
        quantity: product.quantity && product.quantity > 0 ? Math.floor(product.quantity) : 1,
      };
    })
    .filter(Boolean);
};

const buildCheckoutDestination = (items) => {
  if (!Array.isArray(items) || items.length !== 1) {
    return '/checkout';
  }

  const [item] = items;
  const params = new URLSearchParams();
  params.set('product', item.productSlug);
  if (item.planKey) {
    params.set('planKey', item.planKey);
  }
  if (item.quantity && item.quantity !== 1) {
    params.set('quantity', String(item.quantity));
  }
  return `/checkout?${params.toString()}`;
};

export default function useCheckoutRedirect(defaultFlowContext = null) {
  const navigate = useNavigate();
  const location = useLocation();
  const { replaceCart, pending: cartPending } = useCheckoutCart();
  const { catalog, loading: catalogLoading, error: catalogError } = useProductCatalog();
  const [processing, setProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const planIndex = useMemo(() => {
    const map = new Map();
    if (catalog && Array.isArray(catalog.products)) {
      catalog.products.forEach((product) => {
        if (!product || typeof product.slug !== 'string' || !Array.isArray(product.plans)) {
          return;
        }
        map.set(product.slug, product.plans);
      });
    }
    return map;
  }, [catalog]);

  const resolvePlanKey = useCallback(
    (productSlug, preferredPlanKey) => {
      if (!productSlug) {
        return null;
      }
      const plans = planIndex.get(productSlug) || [];
      const fallbackPlanKey = preferredPlanKey || 'default';
      if (!Array.isArray(plans) || plans.length === 0) {
        return fallbackPlanKey;
      }
      if (preferredPlanKey) {
        const explicit = plans.find((plan) => plan.planKey === preferredPlanKey);
        if (explicit) {
          return explicit.planKey;
        }
      }
      const defaultPlan = plans.find((plan) => plan.default) || plans[0];
      return defaultPlan?.planKey || fallbackPlanKey;
    },
    [planIndex],
  );

  const startCheckout = useCallback(
    async ({ products, flowContext, metadata, contact } = {}) => {
      const normalizedProducts = ensureProductList(products);
      if (!normalizedProducts.length) {
        setCheckoutError('No items were provided for checkout.');
        return;
      }

      const resolvedItems = [];
      for (const product of normalizedProducts) {
        const planKey = resolvePlanKey(product.productSlug, product.planKey);
        if (!planKey) {
          setCheckoutError(`Pricing for ${product.productSlug} is not available right now.`);
          return;
        }
        resolvedItems.push({
          productSlug: product.productSlug,
          planKey,
          quantity: product.quantity,
        });
      }

      const checkoutFlowContext = flowContext || defaultFlowContext || null;
      const checkoutMetadata = normalizeMetadata(metadata);
      const checkoutFallback = {
        items: resolvedItems,
        flowContext: checkoutFlowContext,
        metadata: checkoutMetadata,
        source: 'client_checkout_redirect',
      };
      const checkoutDestination = buildCheckoutDestination(resolvedItems);
      const openCheckout = (cartSynced = false) => {
        navigateToCheckoutOverlay(navigate, checkoutDestination, location, {
          state: {
            checkoutCartFallback: {
              ...checkoutFallback,
              cartSynced,
            },
          },
        });
      };

      try {
        setProcessing(true);
        setCheckoutError(null);

        if (contact && (contact.email || contact.name || contact.phone)) {
          const normalizedContact = normalizeContact(contact);
          saveContactToSession(normalizedContact);
        }

        await replaceCart(resolvedItems, {
          flowContext: checkoutFlowContext,
          metadata: checkoutMetadata,
        });

        openCheckout(true);
      } catch (error) {
        console.error('[useCheckoutRedirect] Failed to start checkout', error);
        setCheckoutError('');
        openCheckout(false);
      } finally {
        setProcessing(false);
      }
    },
    [defaultFlowContext, location, navigate, replaceCart, resolvePlanKey],
  );

  return {
    startCheckout,
    processing: processing || cartPending,
    checkoutError,
    setCheckoutError,
    catalogLoading,
    catalogError,
  };
}
